/**
 * gift.js — pagina Regali: ordinamento dei set abbinati + aggiunta del set al
 * carrello.
 *
 * Non c'è più nessun filtro. C'erano un quiz a tre domande (per chi / budget /
 * che tipo) e l'occasione che arrivava dall'indirizzo, con i chip, i parametri
 * in query string e la briciola di pane «Home / Regali / Natale»: sono spariti
 * tutti insieme, e la pagina mostra sempre tutti i set. Un vecchio link con
 * ?occasione=natale continua ad aprirsi senza errori — il parametro non lo legge
 * più nessuno — e atterra sulla pagina intera.
 *
 * Caricato dalla sezione con <script type="module" src="{{ 'gift.js' | asset_url }}">,
 * quindi già versionato. Gli import verso gli altri moduli usano i bare specifier
 * dell'import map: un import relativo risolverebbe a un URL senza ?v=hash, che la
 * CDN Shopify tiene in cache fino a un anno.
 */
import { SECTION_ID, openCartDrawer, renderCartDrawer, updateCartCount } from '@theme/cart-drawer';

/* ============================================================
   Ordinamento
   ============================================================ */

/**
 * I comparatori. Il prezzo arriva da data-total in centesimi (intero), quindi si
 * confronta come numero: sulla stringa formattata da money «100,00 €» starebbe
 * prima di «90,00 €».
 *
 * localeCompare e non < >: i nomi sono italiani e il confronto fra code unit
 * metterebbe «Élan» dopo «Zaffiro». sensitivity 'base' ignora accenti e
 * maiuscole, numeric mette «Set 2» prima di «Set 10».
 *
 * A parità si scende sull'altro criterio invece di lasciare decidere al motore:
 * due set allo stesso prezzo devono restare nello stesso ordine a ogni passaggio,
 * altrimenti la griglia si rimescola da sola tornando sulla stessa scelta.
 */
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function byName(a, b) {
  return collator.compare(a.dataset.name || '', b.dataset.name || '');
}

function byTotal(a, b) {
  return (Number(a.dataset.total) || 0) - (Number(b.dataset.total) || 0);
}

const SORTS = {
  'name-asc': byName,
  'name-desc': (a, b) => byName(b, a),
  'price-asc': (a, b) => byTotal(a, b) || byName(a, b),
  'price-desc': (a, b) => byTotal(b, a) || byName(a, b),
};

function initGrid(root) {
  const bundles = Array.from(root.querySelectorAll('[data-bundle]'));
  /* Zero set: non c'è niente da ordinare, e si esce prima di scoprire il select.
     Al suo posto la sezione ha già stampato «i set arrivano a breve», e un
     comando acceso lì sopra prometterebbe qualcosa da fare che non c'è. */
  if (!bundles.length) return;

  const results = root.querySelector('[data-gift-results]');
  const sortSelect = root.querySelector('[data-gift-sort]');

  // l'ordinamento senza JS sarebbe un select che non riordina niente: il markup
  // lo manda hidden, ed è qui che si può mostrare
  root.querySelectorAll('[data-gift-enhance]').forEach((el) => {
    el.hidden = false;
  });

  // 'default' = l'ordine del metaobject, che è la scelta editoriale di chi
  // compila i set. È quello di partenza e si può tornarci.
  let sort = 'default';

  /**
   * Riordina davvero il DOM invece di giocare con order CSS: la griglia è un
   * auto-fit e un ordine visivo scollegato da quello del documento manderebbe
   * il tab e il lettore di schermo in una sequenza diversa da quella a schermo.
   *
   * Il fragment fa una sola scrittura nel documento invece di N spostamenti.
   *
   * Si ordina sempre una copia: bundles resta nell'ordine del metaobject anche
   * dopo dieci giri di select, ed è quello che «Consigliati» rimette in pagina.
   */
  function paintOrder() {
    if (!results) return;
    const rows = SORTS[sort] ? bundles.slice().sort(SORTS[sort]) : bundles;
    const frag = document.createDocumentFragment();
    rows.forEach((el) => frag.appendChild(el));
    results.appendChild(frag);
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      sort = sortSelect.value;
      paintOrder();
      /* Nessuno scroll: chi ha scelto l'ordine sta già guardando la griglia. */
    });

    /* Al reload il browser ripristina il valore scelto nel select, ma il markup
       che arriva dal server è in ordine editoriale: senza questo la pagina
       tornerebbe con «Prezzo crescente» scritto sopra una griglia che non lo è.
       (Dal bfcache non serve: lì torna il DOM già riordinato.) */
    if (sortSelect.value && sortSelect.value !== sort) {
      sort = sortSelect.value;
      paintOrder();
    }
  }
}

/* ============================================================
   Aggiunta del set al carrello
   ============================================================ */

/* Il corpo di un 422 di /cart/add.js contiene la spiegazione già tradotta da
   Shopify, es. "All 1 Anello Fenice are in your cart." Su un set scatta appena
   uno dei pezzi è esaurito, ed è l'unica informazione utile da mostrare. */
async function addErrorMessage(res) {
  try {
    const data = await res.json();
    return data.description || data.message || '';
  } catch (parseErr) {
    return ''; // corpo non JSON: nessun motivo da mostrare
  }
}

function showError(form, message) {
  const box = form.querySelector('[data-cart-error]');
  if (!box) return false;
  box.textContent = message;
  box.hidden = !message;
  return true;
}

/**
 * Legge gli input nascosti items[i][id] / items[i][quantity] del form e ne
 * ricostruisce l'array. Il form resta un POST valido verso /cart/add anche senza
 * JS; qui si passa invece per /cart/add.js in JSON, che è la forma documentata
 * per aggiungere più articoli in una sola chiamata.
 */
function readItems(form) {
  const items = [];
  form.querySelectorAll('input[name$="[id]"]').forEach((input) => {
    const prefix = input.name.slice(0, input.name.indexOf(']') + 1); // "items[0]"
    const qty = form.querySelector('input[name="' + prefix + '[quantity]"]');
    const id = Number(input.value);
    if (!id) return;
    items.push({ id, quantity: Number(qty && qty.value) || 1 });
  });
  return items;
}

document.addEventListener('submit', async (e) => {
  const form = e.target instanceof Element ? e.target.closest('form[data-gift-set]') : null;
  if (!form) return;

  const items = readItems(form);
  if (!items.length) return; // niente da aggiungere: lascia fare al POST nativo

  e.preventDefault();

  const btn = form.querySelector('[type="submit"]');
  const original = btn && btn.textContent;
  showError(form, ''); // pulisce l'esito del tentativo precedente
  if (btn) {
    btn.disabled = true;
    btn.textContent = btn.dataset.adding || btn.textContent;
  }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // chiede a Shopify anche l'HTML aggiornato del drawer (Section Rendering API)
      body: JSON.stringify({ items, sections: SECTION_ID }),
    });

    if (!res.ok) {
      const reason = await addErrorMessage(res);
      if (reason && showError(form, reason)) return; // il finally ripristina il pulsante
      throw new Error('add failed');
    }

    const added = await res.json();
    if (btn) btn.textContent = btn.dataset.added || 'OK';

    // drawer e contatore sono rifiniture: un loro errore non deve annullare
    // un'aggiunta che è già andata a buon fine
    try {
      renderCartDrawer(added.sections && added.sections[SECTION_ID]);
      openCartDrawer();
      const cartRes = await fetch('/cart.js');
      if (cartRes.ok) updateCartCount(await cartRes.json());
    } catch (refreshErr) {
      /* drawer non aggiornato: gli articoli sono comunque nel carrello */
    }
  } catch (err) {
    if (btn) btn.textContent = 'Riprova';
  } finally {
    setTimeout(() => {
      if (btn) {
        btn.disabled = false;
        btn.textContent = original;
      }
    }, 1400);
  }
});

document.querySelectorAll('[data-gift]').forEach(initGrid);

/* Editor tema: a ogni modifica delle impostazioni Shopify rimpiazza l'HTML della
   sezione, ma il modulo è già valutato e lo <script> reinserito ha lo stesso URL,
   quindi non riparte. Senza questo l'ordinamento resterebbe hidden proprio mentre
   stai lavorando alla pagina.
   Il listener submit no: è delegato su document e sopravvive al ricambio del DOM. */
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof Element) e.target.querySelectorAll('[data-gift]').forEach(initGrid);
});
